import React, { useEffect, useState } from "react";
import {
  CRJMC_PRICE,
  calculateDiscountedPrice,
  PROMO_EXEMPT_URL_PATTERNS,
} from "@/constants";
import ButtonCTA from "./ButtonCTA";
import { usePromo } from "@/contexts/PromoContext";
import PromoCacheService from "../services/PromoCacheService";

// Interface definitions
interface PromotionCodeResponse {
  code: string;
  exists: boolean;
  valid: boolean;
  validFrom: string | null;
  validUntil: string | null;
  discountPercent: number | null;
}

const Promo: React.FC = () => {
  const [promotionData, setPromotionData] =
    useState<PromotionCodeResponse | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [timeUntilExpiration, setTimeUntilExpiration] = useState<string>("");
  const [discountedPrice, setDiscountedPrice] = useState<number | null>(null);
  const [bannerVisible, setBannerVisible] = useState(false);

  // Get the setDiscountPercent function from the context
  let setDiscountPercent: (discountPercent: number | null) => void = () => {};
  try {
    const promoContext = usePromo();
    setDiscountPercent = promoContext.setDiscountPercent;
  } catch (error) {
    // Context not available, use a no-op function
    // Using console.debug instead of console.warn to avoid cluttering the console
    if (process.env.NODE_ENV === "development") {
      console.debug(
        "PromoContext not available, discount functionality will be limited",
      );
    }
  }

  // Function to save promo code using PromoCacheService
  const savePromoCodeToCache = (code: string) => {
    PromoCacheService.savePromoCode(code);
  };

  // Function to get cached promo code using PromoCacheService
  const getCachedPromoCode = (): string | null => {
    return PromoCacheService.getPromoCode();
  };

  // Function to remove cached promo code using PromoCacheService
  const removeCachedPromoCode = () => {
    PromoCacheService.removePromoCode();
  };

  // Function to check if current time is between 00:01 and 06:25
  const isNightTime = (): boolean => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();

    // Convert to minutes since midnight for easier comparison
    const currentTimeInMinutes = hours * 60 + minutes;
    const nightStartInMinutes = 1; // 00:01
    const nightEndInMinutes = 6 * 60 + 25; // 06:25

    // Check if current time is between 00:01 and 06:30
    return (
      currentTimeInMinutes >= nightStartInMinutes &&
      currentTimeInMinutes <= nightEndInMinutes
    );
  };

  // Function to get current day's 06:25
  const getStartOfMorning = (): Date => {
    const todayMorning = new Date();
    todayMorning.setHours(6, 25, 0, 0);
    return todayMorning;
  };

  // Add a state to track if we're on the client side
  const [isClient, setIsClient] = useState(false);

  // This effect runs once after initial render to set isClient to true
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Main effect for promo code handling - only runs on client side after hydration
  useEffect(() => {
    // Skip this effect during server-side rendering or initial client render
    if (!isClient) return;

    // Function to get query parameters from URL
    const getQueryParam = (param: string): string | null => {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(param);
    };

    // Function to check if current URL matches any exempt patterns
    const isUrlExempt = (): boolean => {
      const currentPath = window.location.pathname;
      return PROMO_EXEMPT_URL_PATTERNS.some((pattern) =>
        currentPath.includes(pattern),
      );
    };

    // Get promoCode from URL if it exists, otherwise check localStorage
    const promoCodeFromUrl = getQueryParam("promoCode");

    // Only get cached promo code if URL is not exempt or if promo code is explicitly specified in URL
    const cachedPromoCode =
      !isUrlExempt() || promoCodeFromUrl ? getCachedPromoCode() : null;

    // Use URL promo code if available, otherwise use cached code (if applicable)
    let promoCode = promoCodeFromUrl || cachedPromoCode;

    // Check if it's night time
    const isNight = isNightTime();

    // If it's night time, check for NIGHT20
    if (isNight) {
      // If no promo code is specified, use NIGHT20
      if (!promoCode) {
        promoCode = "NIGHT20";
      }
      // If a promo code is specified and it's not NIGHT20, we'll check NIGHT20 separately
      else if (promoCode !== "NIGHT20") {
        // Store the user's promo code
        const userPromoCode = promoCode;

        // Check NIGHT20 first
        const checkNight20 = async () => {
          try {
            const response = await fetch(
              `${process.env.GATSBY_API_URL}/promotion/code/NIGHT20`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                },
              },
            );

            if (response.status === 200) {
              const night20Data = await response.json();

              // If NIGHT20 is valid, use it (with modified validUntil)
              if (
                night20Data.exists &&
                night20Data.valid &&
                night20Data.discountPercent !== null
              ) {
                // Create a copy of the data to avoid mutating the original
                const modifiedData = { ...night20Data };

                // Replace validUntil with 06:30 next day for display purposes
                const nextMorning = getStartOfMorning();
                modifiedData.validUntil = nextMorning.toISOString();

                // Check if the user's code has a higher discount
                const userResponse = await fetch(
                  `${process.env.GATSBY_API_URL}/promotion/code/${userPromoCode}`,
                  {
                    method: "GET",
                    headers: {
                      "Content-Type": "application/json",
                    },
                  },
                );

                if (userResponse.status === 200) {
                  const userData = await userResponse.json();

                  // If the user's code is valid and has a higher discount, use it instead
                  if (
                    userData.exists &&
                    userData.valid &&
                    userData.discountPercent !== null &&
                    userData.discountPercent > night20Data.discountPercent
                  ) {
                    // Continue with the user's code
                    return;
                  }
                }

                // Use NIGHT20 with modified validUntil
                setPromotionData(modifiedData);

                // Save NIGHT20 to cache for use during the session
                savePromoCodeToCache("NIGHT20");

                // Calculate discounted price
                const newPrice = calculateDiscountedPrice(
                  CRJMC_PRICE,
                  modifiedData.discountPercent,
                );
                setDiscountedPrice(newPrice);

                // Update the discount percent in the context
                setDiscountPercent(modifiedData.discountPercent);

                setShowBanner(true);

                // Don't proceed with checking the user's code
                return;
              }
            }
          } catch (error) {
            console.error("Error checking NIGHT20 code:", error);
          }
        };

        // Check NIGHT20, but don't wait for it
        checkNight20();
      }
    }

    // If we have a promo code (either from URL or cache), query the API
    if (promoCode) {
      const checkPromoCode = async () => {
        try {
          // Use fetch directly to handle different status codes
          const response = await fetch(
            `${process.env.GATSBY_API_URL}/promotion/code/${promoCode}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            },
          );

          // Check response status
          if (response.status === 200) {
            const data: PromotionCodeResponse = await response.json();

            // Special handling for NIGHT20 code during night time
            if (
              promoCode === "NIGHT20" &&
              isNightTime() &&
              data.exists &&
              data.valid
            ) {
              // Create a copy of the data to avoid mutating the original
              const modifiedData = { ...data };

              // Replace validUntil with 06:25 next day for display purposes
              const nextMorning = getStartOfMorning();
              modifiedData.validUntil = nextMorning.toISOString();

              setPromotionData(modifiedData);
            } else {
              setPromotionData(data);
            }

            // Check if promotion is valid and exists
            if (data.exists && data.valid && data.discountPercent !== null) {
              // Special handling for NIGHT20 - only show during night time
              if (promoCode === "NIGHT20" && !isNightTime()) {
                // Don't show NIGHT20 if it's not night time
                removeCachedPromoCode();
                setDiscountPercent(null);
                return;
              }

              // If the code is from URL and not NIGHT20, cache it for future use
              if (promoCodeFromUrl && promoCode !== "NIGHT20") {
                savePromoCodeToCache(promoCodeFromUrl);
              }
              // If it's NIGHT20 and it's night time, cache it for use during the session
              else if (promoCode === "NIGHT20" && isNightTime()) {
                savePromoCodeToCache(promoCode);
              }

              setShowBanner(true);

              // Calculate discounted price
              const newPrice = calculateDiscountedPrice(
                CRJMC_PRICE,
                data.discountPercent,
              );
              setDiscountedPrice(newPrice);

              // Update the discount percent in the context
              setDiscountPercent(data.discountPercent);

              // Hide Brevo Conversations if it exists
              if (window.BrevoConversations && window.BrevoConversations.hide) {
                window.BrevoConversations.hide();
              }
            } else {
              // If the code is no longer valid, remove it from cache
              removeCachedPromoCode();
              setDiscountPercent(null);
            }
          } else if (response.status === 404) {
            // Handle 404 response - remove from cache if it was cached
            console.log("Promotion code not found");
            removeCachedPromoCode();
            setDiscountPercent(null);
          }
        } catch (error) {
          console.error("Error checking promo code:", error);
        }
      };

      checkPromoCode();
    }
  }, [isClient]); // Only run when isClient changes (after hydration)

  // Calculate time until expiration - only runs on client side
  useEffect(() => {
    // Skip during server-side rendering or initial client render
    if (!isClient || !promotionData?.validUntil) return;

    const updateExpirationTime = () => {
      // Get current time
      const now = new Date();

      // Special handling for NIGHT20 - hide if it's no longer night time
      if (promotionData.code === "NIGHT20" && !isNightTime()) {
        setShowBanner(false);
        return;
      }

      // Parse the expiration date from API (which is in UTC)
      // Ensure we're explicitly handling it as UTC by appending 'Z' if it doesn't already have timezone info
      let validUntilString = promotionData.validUntil as string;
      if (!validUntilString.endsWith("Z") && !validUntilString.includes("+")) {
        validUntilString += "Z"; // Append 'Z' to indicate UTC
      }
      const expirationDate = new Date(validUntilString);

      // Create a display expiration date that's 10 minutes shorter for UI purposes
      const displayExpirationDate = new Date(expirationDate);
      displayExpirationDate.setMinutes(displayExpirationDate.getMinutes() - 10);

      // Use the modified date for display calculations
      const diffMs = displayExpirationDate.getTime() - now.getTime();

      // Check if the timer has reached zero or is negative
      if (diffMs <= 0) {
        // Promotion has expired or timer is negative
        setShowBanner(false);
        // Remove the promo code from cache
        removeCachedPromoCode();
        // Reset the discount percent in the context
        setDiscountPercent(null);
        // Reset promotion data
        setPromotionData(null);
        return;
      }

      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(
        (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      // Always use the same format with padded numbers for consistent width
      const formattedDays = diffDays.toString().padStart(2, "0");
      const formattedHours = diffHours.toString().padStart(2, "0");
      const formattedMinutes = diffMinutes.toString().padStart(2, "0");
      const formattedSeconds = diffSeconds.toString().padStart(2, "0");

      setTimeUntilExpiration(
        `${diffDays > 0 ? `${formattedDays}d ` : ""}${formattedHours}h ${formattedMinutes}m ${formattedSeconds}s`,
      );
    };

    updateExpirationTime();
    const intervalId = setInterval(updateExpirationTime, 1000); // Update every second

    return () => clearInterval(intervalId);
  }, [isClient, promotionData]);

  // Effect to check if night has ended and remove NIGHT20 code from cache - only runs on client side
  useEffect(() => {
    // Skip during server-side rendering or initial client render
    if (!isClient) return;

    // Function to check if cached promo code is NIGHT20 and remove it if night has ended
    const checkAndRemoveNight20 = () => {
      const cachedCode = getCachedPromoCode();
      if (cachedCode === "NIGHT20" && !isNightTime()) {
        // If the night has ended and NIGHT20 is in cache, remove it
        removeCachedPromoCode();
        // Also set PromoContext discountPercent to null
        setDiscountPercent(null);
        // Reset promotion data if it's NIGHT20
        if (promotionData?.code === "NIGHT20") {
          setPromotionData(null);
          setShowBanner(false);
        }
        if (process.env.NODE_ENV === "development") {
          console.debug(
            "Night has ended, removed NIGHT20 code from cache and reset discount",
          );
        }
      }
    };

    // Check immediately on mount
    checkAndRemoveNight20();

    // Set up interval to check periodically (every minute)
    const intervalId = setInterval(checkAndRemoveNight20, 10000);

    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [isClient, promotionData]);

  // Effect to handle animation when showBanner changes - only runs on client side
  useEffect(() => {
    // Skip during server-side rendering or initial client render
    if (!isClient) return;

    if (showBanner) {
      // Small delay to ensure DOM is ready before animation
      const timer = setTimeout(() => {
        setBannerVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setBannerVisible(false);
    }
  }, [isClient, showBanner]);

  // Don't render anything on server-side or if banner shouldn't be shown
  if (!isClient || !showBanner) {
    return null;
  }

  // Define the animation styles
  const bannerStyle = {
    transform: bannerVisible ? "translateY(0)" : "translateY(100%)",
    transition: "transform 0.5s ease-out",
  };

  return (
    <div
      className="fixed bottom-0 pb-10 md:pb-4 left-0 right-0 bg-emerald-600 text-white p-4 z-50 shadow-lg flex flex-col md:flex-row justify-between items-center"
      style={bannerStyle}
    >
      <div className="text-center md:text-left mb-3 md:mb-0">
        <span className="font-bold">Discount active!</span>
        {promotionData?.validUntil !== null && (
          <>
            {" "}
            Expires in:{" "}
            <span
              className="font-mono"
              style={{ display: "inline-block", minWidth: "160px" }}
            >
              {timeUntilExpiration}
            </span>
          </>
        )}
      </div>
      <ButtonCTA
        showSubheader={false}
        className="bg-white text-emerald-700 px-4 py-2 rounded-md font-medium hover:bg-gray-100 transition-colors"
        discountPercent={
          promotionData?.discountPercent !== null
            ? promotionData?.discountPercent
            : undefined
        }
      />
    </div>
  );
};

export default Promo;
